import React from "react";
import { Link } from "react-router-dom";
import { FiLayout, FiRepeat, FiCode, FiCheck, FiArrowRight } from "react-icons/fi";

const services = [
  {
    icon: FiLayout,
    title: "Microsoft Dynamics 365 Business Central",
    description:
      "Unify finance, sales, purchasing, inventory and operations in one ERP platform configured around your processes.",
    capabilities: [
      "Cloud and on-premises implementation",
      "Finance, GST, inventory and operational configuration",
      "User training, testing and go-live support",
    ],
    cta: "Explore Business Central",
    to: "/contactus",
    accent: {
      top: "border-t-blue-500",
      iconBg: "bg-blue-500/15",
      iconText: "text-blue-300",
      iconGlow: "shadow-blue-500/30",
      check: "bg-blue-500/20 text-blue-300",
      cta: "text-blue-300",
    },
  },
  {
    icon: FiRepeat,
    title: "Tally to Business Central Migration",
    description:
      "Move critical data from Tally to Business Central through a structured process focused on accuracy and continuity.",
    capabilities: [
      "Master and transaction data migration",
      "Opening balances and outstanding-document validation",
      "Reconciliation and parallel-run assistance",
    ],
    cta: "Explore Migration Services",
    to: "/contactus",
    accent: {
      top: "border-t-cyan-500",
      iconBg: "bg-cyan-500/15",
      iconText: "text-cyan-300",
      iconGlow: "shadow-cyan-500/30",
      check: "bg-cyan-500/20 text-cyan-300",
      cta: "text-cyan-300",
    },
  },
  {
    icon: FiCode,
    title: "Custom Software & Automation",
    description:
      "Build secure web, mobile and workflow solutions that solve practical challenges and connect teams and information.",
    capabilities: [
      "Web and mobile application development",
      "Workflow automation and system integrations",
      "Reporting dashboards and business tools",
    ],
    cta: "Explore Custom Solutions",
    to: "/software-development",
    accent: {
      top: "border-t-violet-500",
      iconBg: "bg-violet-500/15",
      iconText: "text-violet-300",
      iconGlow: "shadow-violet-500/30",
      check: "bg-violet-500/20 text-violet-300",
      cta: "text-violet-300",
    },
  },
];

const WhatWeDo = () => {
  return (
    <section className="relative overflow-hidden bg-[#060e24] py-20 md:py-24 lg:py-28">
      {/* Dot pattern */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
        aria-hidden="true"
      />

      {/* Ambient glows */}
      <div
        className="pointer-events-none absolute -left-32 top-1/4 h-[30rem] w-[40rem] rounded-full bg-blue-600/15 blur-[140px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-40 bottom-0 h-[30rem] w-[40rem] rounded-full bg-violet-600/15 blur-[140px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-[1340px] px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="about-fade-up mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm font-bold tracking-[0.12em] text-slate-300 uppercase shadow-sm backdrop-blur-sm">
            WHAT WE DO
          </span>

          <h2 className="mt-5 font-raleway font-extrabold leading-[1.12] tracking-tight text-white text-[clamp(1.75rem,4vw,2.75rem)]">
            Three capabilities. One complete{" "}
            <span className="text-[#FFDB16] drop-shadow-[0_0_20px_rgba(255,219,22,0.4)]">
              digital transformation
            </span>{" "}
            partner.
          </h2>

          <p className="mt-5 font-openSans text-sm leading-relaxed text-slate-300 md:text-base">
            From modern ERP implementation and secure data migration to
            custom-built digital solutions, Abscod helps businesses simplify
            operations, improve visibility and scale with confidence.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-14 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, index) => (
            <article
              key={service.title}
              className={`about-fade-up about-delay-${index + 1} group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 border-t-4 ${service.accent.top} bg-white/[0.04] p-8 shadow-[0_8px_40px_-20px_rgba(0,0,0,0.4)] backdrop-blur-md transition-all duration-300 hover:-translate-y-2 hover:bg-white/[0.07] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.5)]`}
            >
              {/* Icon */}
              <span
                className={`flex h-16 w-16 items-center justify-center rounded-2xl ${service.accent.iconBg} ${service.accent.iconText} shadow-[0_0_30px_-8px] ${service.accent.iconGlow} backdrop-blur-sm transition-transform duration-300 group-hover:scale-110`}
              >
                <service.icon className="h-6 w-6" strokeWidth={2} />
              </span>

              {/* Content */}
              <h3 className="mt-6 font-raleway text-xl font-bold text-white">
                {service.title}
              </h3>
              <p className="mt-3 font-openSans text-sm leading-relaxed text-slate-300">
                {service.description}
              </p>

              <ul className="mt-5 space-y-3">
                {service.capabilities.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 font-openSans text-sm text-slate-300"
                  >
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${service.accent.check}`}
                    >
                      <FiCheck className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                to={service.to}
                className={`mt-8 inline-flex items-center gap-2 font-openSans text-sm font-bold ${service.accent.cta} transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#060e24]`}
              >
                {service.cta}
                <FiArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={2.5}
                />
              </Link>
            </article>
          ))}
        </div>

        {/* Stats */}
        <div className="about-fade-up mt-16 grid grid-cols-2 gap-6 rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-10 backdrop-blur-md md:grid-cols-4 md:gap-4 md:px-10 md:py-12">
          {[
            { value: "50", suffix: "+", label: "Projects Launched" },
            { value: "200", suffix: "+", label: "Satisfied Customers" },
            { value: "3", suffix: "+", label: "Years Experience" },
            { value: "98", suffix: "%", label: "Client Retention" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-heading text-4xl font-extrabold tracking-tight text-white md:text-5xl">
                {stat.value}
                <span className="text-[#FFDB16]">{stat.suffix}</span>
              </p>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-400 md:text-sm">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
